/**
 * useProposal.ts
 *
 * React hooks for the Proposal AI Draft Review module.
 *
 * Rules enforced:
 * - Uses @jinzhuan/api-client exclusively — no hand-written fetch/axios.
 * - No fake/optimistic state — all mutations await server confirmation.
 * - HTTP 409 surfaces a human-readable conflict message.
 */
import { useState, useCallback } from 'react';
import { createApiClient, ApiError as ApiClientError } from '@/lib/adminApiClient';
import type { ApiComponents } from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

// 方案模块端点都受 RBAC + 租户隔离保护；typed client 必须通过共享的
// tokenStorage 流向请求附加 `Authorization: Bearer <accessToken>`（Issue #226）。
const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

// ============================================================================
// Type aliases
// ============================================================================

type ProposalDto = ApiComponents['schemas']['ProposalDto'];
type CreateProposalDto = ApiComponents['schemas']['CreateProposalDto'];
type UpdateProposalDto = ApiComponents['schemas']['UpdateProposalDto'];
type TransitionProposalDto = ApiComponents['schemas']['TransitionProposalDto'];
type ProposalVersionDto = ApiComponents['schemas']['ProposalVersionDto'];
type CreateProposalVersionDto = ApiComponents['schemas']['CreateProposalVersionDto'];
type GenerateAiDraftDto = ApiComponents['schemas']['GenerateAiDraftDto'];
type AiDraftResultDto = ApiComponents['schemas']['AiDraftResultDto'];
type ProposalReviewDto = ApiComponents['schemas']['ProposalReviewDto'];
type CreateProposalReviewDto = ApiComponents['schemas']['CreateProposalReviewDto'];

// ============================================================================
// Error helpers
// ============================================================================

export interface ApiError {
  message: string;
  code?: string;
}

function toApiError(err: unknown, httpStatus?: number): ApiError {
  // openapi-fetch middleware throws ApiClientError on non-2xx responses, so
  // the catch path supplies the status via the thrown error instead of via
  // the explicit `httpStatus` argument. Fall back to it when the caller did
  // not pass an explicit status (Issue #226).
  const status = httpStatus ?? (err instanceof ApiClientError ? err.status : undefined);
  if (status === 401) {
    return { message: '会话已失效，请重新登录后再试', code: 'UNAUTHENTICATED' };
  }
  if (status === 403) {
    return { message: '当前角色没有方案模块访问权限', code: 'FORBIDDEN' };
  }
  if (status === 409) {
    return {
      message: '数据已被其他用户修改，请刷新页面后重试',
      code: 'CONFLICT',
    };
  }
  if (err instanceof Error) {
    return { message: err.message };
  }
  return { message: String(err) };
}

// ============================================================================
// Proposal list hook
// ============================================================================

export function useProposalList() {
  const [proposals, setProposals] = useState<ProposalDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  type ProposalStatusFilter = 'draft' | 'in_review' | 'approved' | 'rejected' | 'locked' | 'cancelled';

  const fetchProposals = useCallback(
    async (params?: { status?: ProposalStatusFilter; customerId?: string; page?: number; limit?: number }) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.GET('/api/v1/proposals', {
          params: {
            query: {
              ...(params?.status ? { status: params.status } : {}),
              ...(params?.customerId ? { customerId: params.customerId } : {}),
              ...(params?.page ? { page: params.page } : {}),
              ...(params?.limit ? { limit: params.limit } : {}),
            },
          },
        });
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return;
        }
        setProposals([...(data ?? [])]);
      } catch (e) {
        setError(toApiError(e));
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { proposals, loading, error, fetchProposals };
}

// ============================================================================
// Proposal detail hook
// ============================================================================

export function useProposalDetail() {
  const [proposal, setProposal] = useState<ProposalDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchProposal = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.GET('/api/v1/proposals/{id}', {
        params: { path: { id } },
      });
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return;
      }
      setProposal(data ?? null);
    } catch (e) {
      setError(toApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  return { proposal, loading, error, fetchProposal };
}

// ============================================================================
// Create proposal hook
// ============================================================================

export function useCreateProposal() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const createProposal = useCallback(
    async (dto: CreateProposalDto): Promise<ProposalDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST('/api/v1/proposals', {
          body: dto,
        });
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { createProposal, loading, error };
}

// ============================================================================
// Update proposal hook
// ============================================================================

export function useUpdateProposal() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const updateProposal = useCallback(
    async (id: string, dto: UpdateProposalDto): Promise<ProposalDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.PUT('/api/v1/proposals/{id}', {
          params: { path: { id } },
          body: dto,
        });
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { updateProposal, loading, error };
}

// ============================================================================
// Proposal status transition hooks
// ============================================================================

export function useTransitionProposal() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const submitForReview = useCallback(
    async (id: string, dto: TransitionProposalDto): Promise<ProposalDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/proposals/{id}/submit-review',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const lockProposal = useCallback(
    async (id: string, dto: TransitionProposalDto): Promise<ProposalDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/proposals/{id}/lock',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { submitForReview, lockProposal, loading, error };
}

// ============================================================================
// Proposal versions hook
// ============================================================================

export function useProposalVersions() {
  const [versions, setVersions] = useState<ProposalVersionDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchVersions = useCallback(async (proposalId: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.GET(
        '/api/v1/proposals/{id}/versions',
        { params: { path: { id: proposalId } } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return;
      }
      setVersions([...(data ?? [])]);
    } catch (e) {
      setError(toApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const createVersion = useCallback(
    async (proposalId: string, dto: CreateProposalVersionDto): Promise<ProposalVersionDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/proposals/{id}/versions',
          { params: { path: { id: proposalId } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { versions, loading, error, fetchVersions, createVersion };
}

// ============================================================================
// AI Draft generation hook
// ============================================================================

export function useGenerateAiDraft() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const generateAiDraft = useCallback(
    async (proposalId: string, dto: GenerateAiDraftDto): Promise<AiDraftResultDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/proposals/{id}/ai-draft',
          { params: { path: { id: proposalId } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { generateAiDraft, loading, error };
}

// ============================================================================
// Proposal reviews hook
// ============================================================================

export function useProposalReviews() {
  const [reviews, setReviews] = useState<ProposalReviewDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchReviews = useCallback(async (proposalId: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.GET(
        '/api/v1/proposals/{id}/reviews',
        { params: { path: { id: proposalId } } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return;
      }
      setReviews([...(data ?? [])]);
    } catch (e) {
      setError(toApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const submitReview = useCallback(
    async (proposalId: string, dto: CreateProposalReviewDto): Promise<ProposalReviewDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/proposals/{id}/reviews',
          { params: { path: { id: proposalId } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { reviews, loading, error, fetchReviews, submitReview };
}

// Re-export status type for use in pages
export type { ProposalDto, ProposalVersionDto, ProposalReviewDto };
